/**
 * Meta-note schema. PRD-003 DATA-07 + PR-34 + PR-36 + PR-37.
 *
 * Four typed content columns (skeleton, voice_decisions, what_left_out,
 * do_yourself_next_time) plus four state pills, plus a working_notes
 * scratchpad column. Each section is independently auto-saved (TECH-11).
 */

import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common.js';
import { metaNoteSectionStateSchema } from './enums.js';

export const metaNoteSchema = z.object({
  id: uuidSchema,
  task_id: uuidSchema,

  // Four content sections: text, draftable independently.
  skeleton: z.string().nullable(),
  voice_decisions: z.string().nullable(),
  what_left_out: z.string().nullable(),
  do_yourself_next_time: z.string().nullable(),

  // Four state pills.
  skeleton_state: metaNoteSectionStateSchema.default('not_started'),
  voice_decisions_state: metaNoteSectionStateSchema.default('not_started'),
  what_left_out_state: metaNoteSectionStateSchema.default('not_started'),
  do_yourself_next_time_state: metaNoteSectionStateSchema.default('not_started'),

  // Sources line: the rendered "Sources: ..." string. Independent from the
  // structured sources array on deliverables (DATA-17); both are populated
  // together at delivery time.
  sources_line: z.string().nullable(),

  // Scratchpad / working notes. Operator-only. Not embedded in the
  // deliverable. PRD-003 PR-35.
  working_notes: z.string().nullable(),

  // Timestamp of the last working-notes auto-save. Shipped in migration 0003;
  // mirrored here for parity (L-007 / TECH-26 reconciliation).
  working_notes_saved_at: isoTimestampSchema.nullable(),

  created_at: isoTimestampSchema,
  updated_at: isoTimestampSchema,
  deleted_at: isoTimestampSchema.nullable(),
});
export type MetaNote = z.infer<typeof metaNoteSchema>;

/**
 * Section identifier for the four-section editor. Used by Studio MCP write
 * tools (T-067 write_meta_note_section) to target a single section per call.
 */
export const metaNoteSectionSchema = z.enum([
  'skeleton',
  'voice_decisions',
  'what_left_out',
  'do_yourself_next_time',
]);
export type MetaNoteSection = z.infer<typeof metaNoteSectionSchema>;

/**
 * Predicate: meta-note is ready to deliver iff every section state is at
 * least draft_done. PRD-003 PR-37.
 */
export const isReadyToDeliver = (note: MetaNote): boolean => {
  const sectionStates = [
    note.skeleton_state,
    note.voice_decisions_state,
    note.what_left_out_state,
    note.do_yourself_next_time_state,
  ];
  return sectionStates.every(
    (s) => s === 'draft_done' || s === 'ready_to_send',
  );
};
